import { ChevronLeft, ChevronRight } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════
   LE PIED DE TABLEAU

   Deux choses, et rien d'autre : combien d'éléments on regarde, et de
   quoi changer de page.

   Le décompte n'est pas décoratif. Sans lui, un tableau filtré qui
   affiche dix lignes ne dit pas s'il en cache deux cents — on croit
   avoir tout vu. « 10 sur 248 » répond à la question avant qu'on la
   pose, et « 10 organisations » suffit quand il n'y a pas de filtre.

   La pagination ne s'affiche que s'il y a plus d'une page : deux
   flèches inertes sous un tableau de six lignes n'apportent rien.
   ═══════════════════════════════════════════════════════════════════════ */

export default function PiedTableau({
  affiches,
  total,
  nom = 'éléments',
  page = 1,
  pages = 1,
  onPage,
}) {
  const filtre = total != null && total !== affiches;
  /* L'accord se fait sur le nombre RÉELLEMENT qualifié : « 1 sur 248
     organisations » est juste, « 1 transactions » ne l'est pas. Tous les
     noms employés ici sont réguliers, retirer le « s » final suffit. */
  const compte = filtre ? total : affiches;
  const accorde = compte > 1 ? nom : nom.replace(/s$/, '');
  const decompte = filtre
    ? `${affiches} sur ${total} ${accorde}`
    : `${affiches} ${accorde}`;

  // Au-delà de sept pages on ne les liste pas toutes : on garde les
  // voisines de la page courante, les extrêmes, et on marque les trous.
  const numeros = [];
  if (pages <= 7) {
    for (let i = 1; i <= pages; i += 1) numeros.push(i);
  } else {
    const autour = [page - 1, page, page + 1].filter((i) => i > 1 && i < pages);
    const brut = [1, ...autour, pages];
    brut.forEach((i, k) => {
      if (k && i - brut[k - 1] > 1) numeros.push('…');
      numeros.push(i);
    });
  }

  return (
    <div className="flex items-center gap-3 px-6 py-3.5 border-t border-[var(--border)]">
      <span className="flex-1 text-[12.5px] text-[var(--text-muted)]">{decompte}</span>

      {pages > 1 && (
        <nav className="flex gap-1.5" aria-label="Pagination">
          <button
            type="button"
            onClick={() => onPage?.(page - 1)}
            disabled={page <= 1}
            aria-label="Page précédente"
            className="w-9 h-9 rounded-controle grid place-items-center border border-[var(--border)]
                       bg-[var(--bg-card)] text-[var(--text-bold)] disabled:opacity-45
                       hover:bg-[var(--bg-main)] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2} />
          </button>

          {numeros.map((n, i) => (n === '…' ? (
            <span key={`trou-${i}`} aria-hidden="true"
                  className="w-9 h-9 grid place-items-center text-[13px] text-[var(--text-muted)]">
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              onClick={() => onPage?.(n)}
              aria-current={n === page ? 'page' : undefined}
              className={`w-9 h-9 rounded-controle text-[13px] font-semibold transition-colors ${
                n === page
                  ? 'bg-[var(--nuit)] text-white'
                  : 'border border-[var(--border)] bg-[var(--bg-card)] text-[var(--text-bold)] hover:bg-[var(--bg-main)]'
              }`}
            >
              {n}
            </button>
          )))}

          <button
            type="button"
            onClick={() => onPage?.(page + 1)}
            disabled={page >= pages}
            aria-label="Page suivante"
            className="w-9 h-9 rounded-controle grid place-items-center border border-[var(--border)]
                       bg-[var(--bg-card)] text-[var(--text-bold)] disabled:opacity-45
                       hover:bg-[var(--bg-main)] transition-colors"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </button>
        </nav>
      )}
    </div>
  );
}
